import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AddShoppingCart, Close, DeleteOutline, Search } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";

import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/permissionUtils";
import { cashService, customersService, inventoryService, productsService, saleService } from "../../services";
import CustomSnackBar from "../CustomSnackBar";
import { emitCashSessionChanged, onCashSessionChanged } from "../../services/http/cashEvents";
import SalePaymentDialog from "./SalePaymentDialog";
import PriceOverrideDialog from "./PriceOverrideDialog";
import usePostSaleInvoiceFlow from "../invoice/usePostSaleInvoiceFlow";
import CustomerQuickCreateDialog from "../customers/CustomerQuickCreateDialog";
import {
  buildStockDetailPatch,
  buildPresentationOptions,
  buildProductStockMeta,
  getMaxUnitsFromStockBase,
  getStockAvailabilityMeta,
  nowDateString,
  selectPreferredPresentationOption,
} from "./saleStockUtils";
import { saveSalesDraft } from "../../state/salesDraftsSlice";
import {
  buildSalesDraftPath,
  createSalesDraftId,
  getSalesDraftStorageKey,
} from "../../state/salesDraftUtils";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const sanitizeIntegerInput = (value) => {
  const next = String(value ?? "").replace(/[^\d]/g, "");
  if (!next) return "";
  return String(Math.max(1, parseInt(next, 10)));
};

const PHONE_MAX_LENGTH = 20;
const normalizePhoneValue = (value) => String(value || "").replace(/[^\d]/g, "").slice(0, PHONE_MAX_LENGTH);

const createTempId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

const buildCartLineTotal = (qtyPresentation, unitPrice) => {
  const qty = Number(qtyPresentation || 0);
  const price = Number(unitPrice || 0);
  return Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0;
};

const getTicketCashierName = (sale) =>
  sale?.sold_by_display ||
  sale?.created_by_name ||
  sale?.authorized_by_display ||
  "-";

const normalizeCartItem = (item) => {
  const qtyPresentation = Math.max(1, parseInt(item?.qty_presentation || 1, 10));
  const unitPrice = Number(item?.unit_price || 0);
  const qtyInBase = Number(item?.qty_in_base || 1);
  return {
    ...item,
    qty: qtyPresentation,
    qty_presentation: qtyPresentation,
    qty_base: qtyPresentation * qtyInBase,
    unit_price: unitPrice,
    line_total: buildCartLineTotal(qtyPresentation, unitPrice),
  };
};

const extractErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }

  const shortages = toArray(error?.response?.data?.shortages);
  if (shortages.length > 0) {
    const names = shortages
      .map((row) => row?.product || row?.detail || null)
      .filter(Boolean)
      .join(", ");
    if (names) return `Stock insuficiente en: ${names}`;
  }

  const itemErrors = toArray(error?.response?.data?.item_errors);
  if (itemErrors.length > 0) {
    const names = itemErrors
      .map((row) => row?.product || row?.detail || null)
      .filter(Boolean)
      .join(", ");
    if (names) return names;
  }

  return fallbackMessage;
};

const GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE =
  "Inventario general activo. Puedes consultar productos, pero las ventas de esta sucursal estan suspendidas.";
const SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE =
  "Producto en conteo activo. Puedes consultar precio y formatos, pero no venderlo hasta terminar el conteo.";

export default function POSDialog({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;

  const [cashInfo, setCashInfo] = useState(null);
  const [loadingCash, setLoadingCash] = useState(false);
  const [salesConfig, setSalesConfig] = useState({ allow_cashier_queue: true });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchStockByProduct, setSearchStockByProduct] = useState({});
  const [presentationOptionsById, setPresentationOptionsById] = useState({});
  const [stockByPresentation, setStockByPresentation] = useState({});
  const [stockDetailByPresentation, setStockDetailByPresentation] = useState({});
  const [stockCountBlock, setStockCountBlock] = useState({
    mode: null,
    countId: null,
    blockedPresentationIds: {},
    blockedProductIds: {},
  });
  const stockByPresentationRef = useRef({});
  const stockDetailByPresentationRef = useRef({});
  const productDetailByIdRef = useRef({});
  const productOptionsByIdRef = useRef({});

  const [cartItems, setCartItems] = useState([]);
  const [cartCustomer, setCartCustomer] = useState("");
  const [cartCustomerPhone, setCartCustomerPhone] = useState("");
  const [cartCustomerId, setCartCustomerId] = useState(null);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerLookupStatus, setCustomerLookupStatus] = useState("idle");
  const [customerCreateDialogOpen, setCustomerCreateDialogOpen] = useState(false);
  const [qtyDraftByItem, setQtyDraftByItem] = useState({});
  const [priceDraftByItem, setPriceDraftByItem] = useState({});
  const [lastValidPriceByItem, setLastValidPriceByItem] = useState({});
  const [editingPriceItemId, setEditingPriceItemId] = useState(null);
  const [itemUpdating, setItemUpdating] = useState(false);

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [transferAccounts, setTransferAccounts] = useState([]);
  const [loadingTransferAccounts, setLoadingTransferAccounts] = useState(false);
  const [priceOverrideDialog, setPriceOverrideDialog] = useState({
    open: false,
    item: null,
    newUnitPrice: null,
    basePrice: null,
    costPrice: null,
  });

  const [openCashDialogOpen, setOpenCashDialogOpen] = useState(false);
  const [openCashSubmitting, setOpenCashSubmitting] = useState(false);
  const [openCashForm, setOpenCashForm] = useState({ opening_float: "0.00", opening_note: "" });
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);
  const { handlePostSaleInvoiceFlow, invoiceDialog } = usePostSaleInvoiceFlow({ showSnack });

  const handleCustomerSelected = useCallback((customer, successMessage = "") => {
    const customerId = Number(customer?.id);
    setCartCustomer(String(customer?.name || "").trim());
    setCartCustomerPhone(normalizePhoneValue(customer?.phone_number || ""));
    setCartCustomerId(Number.isInteger(customerId) && customerId > 0 ? customerId : null);
    setCustomerLookupStatus("found");
    setCustomerCreateDialogOpen(false);
    if (successMessage) {
      showSnack(successMessage, "success");
    }
  }, [showSnack]);

  const handleCustomerPhoneChange = useCallback((event) => {
    const rawPhone = event?.target?.value ?? "";
    const normalizedPhone = normalizePhoneValue(rawPhone);
    const currentPhone = normalizePhoneValue(cartCustomerPhone);
    const keepCurrentCustomer =
      normalizedPhone &&
      currentPhone &&
      normalizedPhone === currentPhone &&
      cartCustomerId;
    setCartCustomerPhone(normalizedPhone);
    setCartCustomerId(keepCurrentCustomer ? cartCustomerId : null);
    setCartCustomer(keepCurrentCustomer ? cartCustomer : "");
    setCustomerLookupStatus(normalizedPhone ? "typing" : "idle");
  }, [cartCustomer, cartCustomerId, cartCustomerPhone]);

  const customerPhoneQuery = useMemo(
    () => normalizePhoneValue(cartCustomerPhone),
    [cartCustomerPhone]
  );

  const customerDisplayValue = useMemo(() => {
    return String(cartCustomer || "").trim() || "CF";
  }, [cartCustomer]);

  const customerLookupHelperText = useMemo(() => {
    if (customerLookupLoading) return "Buscando cliente por telefono...";
    if (customerLookupStatus === "found") return "Cliente encontrado y asociado automaticamente.";
    if (customerPhoneQuery && customerLookupStatus === "not_found") {
      return "No existe cliente con ese telefono. Si no lo creas, la venta seguira como CF.";
    }
    return "Sin telefono el cobro se registra como CF.";
  }, [customerLookupLoading, customerLookupStatus, customerPhoneQuery]);

  const hasOpenCashSession = Boolean(cashInfo?.session && cashInfo.session.status === "OPEN");
  const hasCartItems = cartItems.length > 0;
  const canPayNow = hasPermission(effectivePerms, "sale_pay_now", isSuperuser);
  const cashierQueueEnabled = salesConfig?.allow_cashier_queue !== false;
  const canOpenCash = hasPermission(effectivePerms, "cash_open", isSuperuser);
  const cashOpenBlockedByGeneralInventory = stockCountBlock.mode === "GENERAL";
  const canOverridePrice = Boolean(
    hasPermission(effectivePerms, "sale_discount_override", isSuperuser)
  );
  const itemLimitReached = cartItems.length >= 10;
  const cartSubtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + Number(item?.line_total || 0), 0),
    [cartItems]
  );
  const cartTotal = cartSubtotal;
  const addedProductIds = useMemo(
    () => new Set(cartItems.map((item) => String(item?.product_id || "")).filter(Boolean)),
    [cartItems]
  );
  const getProductCountBlockMeta = useCallback(
    (product) => {
      if (stockCountBlock.mode === "GENERAL") {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const productId = String(product?.id || "");
      if (productId && stockCountBlock.blockedProductIds[productId]) {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const options =
        productOptionsByIdRef.current[productId] ||
        buildPresentationOptions(product);
      const hasBlockedPresentation = options.some(
        (option) => stockCountBlock.blockedPresentationIds[String(option?.id || "")]
      );
      if (hasBlockedPresentation) {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      return { blocked: false, label: "", detail: "" };
    },
    [stockCountBlock]
  );
  const getItemCountBlockMeta = useCallback(
    (item) => {
      if (stockCountBlock.mode === "GENERAL") {
        return {
          blocked: true,
          label: "En conteo",
          detail: GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const presentationId = String(item?.product_presentation_id || "");
      const productId = String(item?.product_id || "");
      if (
        (presentationId && stockCountBlock.blockedPresentationIds[presentationId]) ||
        (productId && stockCountBlock.blockedProductIds[productId])
      ) {
        return {
          blocked: true,
          label: "En conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      return { blocked: false, label: "", detail: "" };
    },
    [stockCountBlock]
  );
  const hasBlockedCartItems = useMemo(
    () => cartItems.some((item) => getItemCountBlockMeta(item).blocked),
    [cartItems, getItemCountBlockMeta]
  );

  useEffect(() => {
    stockByPresentationRef.current = stockByPresentation;
  }, [stockByPresentation]);

  useEffect(() => {
    stockDetailByPresentationRef.current = stockDetailByPresentation;
  }, [stockDetailByPresentation]);

  useEffect(() => {
    setLastValidPriceByItem((prev) => {
      const next = {};
      cartItems.forEach((item) => {
        const key = String(item.tempId);
        next[key] = prev[key] !== undefined ? prev[key] : Number(item?.unit_price || 0).toFixed(2);
      });
      return next;
    });
  }, [cartItems]);

  const mergeStockByPresentation = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setStockByPresentation((prev) => {
      const next = { ...prev, ...patch };
      stockByPresentationRef.current = next;
      return next;
    });
  }, []);

  const mergeStockDetailByPresentation = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setStockDetailByPresentation((prev) => {
      const next = { ...prev, ...patch };
      stockDetailByPresentationRef.current = next;
      return next;
    });
  }, []);

  const registerPresentationOptions = useCallback((productId, options) => {
    if (!Array.isArray(options) || options.length === 0) return;
    if (productId != null) {
      productOptionsByIdRef.current[String(productId)] = options;
    }
    setPresentationOptionsById((prev) => {
      const next = { ...prev };
      options.forEach((option) => {
        next[String(option.id)] = options;
      });
      return next;
    });
  }, []);

  const ensureStockForPresentationIds = useCallback(
    async (presentationIds = [], options = {}) => {
      if (!branchId) return {};
      const uniqueIds = Array.from(
        new Set(
          presentationIds
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value) && value > 0)
        )
      );
      if (uniqueIds.length === 0) return {};

      const forceRefresh = Boolean(options?.forceRefresh);
      const current = stockByPresentationRef.current || {};
      const idsToFetch = forceRefresh
        ? uniqueIds
        : uniqueIds.filter((id) => current[String(id)] === undefined);
      if (idsToFetch.length > 0) {
        const rows = toArray(
          await inventoryService.listStocks({
            branch_id: branchId,
            product_presentation_ids: idsToFetch.join(","),
          })
        );
        const fetched = {};
        rows.forEach((row) => {
          fetched[String(row?.product_presentation_id)] = Number(row?.qty_available ?? row?.qty_base ?? 0);
        });
        idsToFetch.forEach((id) => {
          const key = String(id);
          if (fetched[key] === undefined) {
            fetched[key] = 0;
          }
        });
        mergeStockByPresentation(fetched);
        mergeStockDetailByPresentation(buildStockDetailPatch(rows));
      }

      const latest = stockByPresentationRef.current || {};
      return uniqueIds.reduce((accumulator, id) => {
        accumulator[String(id)] = Number(latest[String(id)] || 0);
        return accumulator;
      }, {});
    },
    [branchId, mergeStockByPresentation, mergeStockDetailByPresentation]
  );

  const fetchStockForPresentation = useCallback(
    async (presentationId) => {
      const stockMap = await ensureStockForPresentationIds([presentationId], { forceRefresh: true });
      return Number(stockMap[String(presentationId)] || 0);
    },
    [ensureStockForPresentationIds]
  );

  const loadPresentationOptionsForProduct = useCallback(
    async (productId) => {
      const key = String(productId || "");
      const cachedOptions = productOptionsByIdRef.current[key];
      if (Array.isArray(cachedOptions) && cachedOptions.length > 0) {
        return cachedOptions;
      }

      let detail = productDetailByIdRef.current[key];
      if (!detail) {
        detail = searchResults.find((row) => String(row?.id) === key) || null;
      }
      if (!detail) {
        detail = await productsService.get(productId);
      }
      if (detail) {
        productDetailByIdRef.current[key] = detail;
      }

      const options = buildPresentationOptions(detail);
      registerPresentationOptions(productId, options);
      return options;
    },
    [registerPresentationOptions, searchResults]
  );

  const getItemOptions = useCallback(
    (item) => {
      const key = String(item?.product_presentation_id || "");
      const mapped = presentationOptionsById[key];
      if (Array.isArray(mapped) && mapped.length > 0) return mapped;

      const fallbackOption = {
        id: item?.product_presentation_id,
        product_id: item?.product_id,
        name: item?.presentation_name || "Presentacion",
        qty_in_base: Number(item?.qty_in_base || 0),
        cost_price: Number(item?.cost_price || 0),
        base_sale_price: Number(item?.base_price ?? item?.unit_price ?? 0),
      };
      return fallbackOption.id ? [fallbackOption] : [];
    },
    [presentationOptionsById]
  );

  const getItemPricingContext = useCallback(
    (item) => {
      const options = getItemOptions(item);
      const selected = options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
      const costPrice = Number(selected?.cost_price ?? item?.cost_price ?? 0);
      const basePrice = Number(selected?.base_sale_price ?? item?.base_price ?? item?.unit_price ?? costPrice);
      return {
        options,
        costPrice: Number.isFinite(costPrice) ? costPrice : 0,
        basePrice: Number.isFinite(basePrice) ? basePrice : 0,
      };
    },
    [getItemOptions]
  );

  const getQtyInBaseForItem = useCallback(
    (item) => {
      const options = getItemOptions(item);
      const selected = options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
      const qtyInBase = Number(selected?.qty_in_base ?? item?.qty_in_base ?? 0);
      if (!Number.isFinite(qtyInBase) || qtyInBase <= 0) return 1;
      return qtyInBase;
    },
    [getItemOptions]
  );

  const getMaxUnitsForOption = useCallback(
    async (option) => {
      const presentationId = Number(option?.id || option?.product_presentation_id);
      const qtyInBase = Number(option?.qty_in_base || 0);
      if (!Number.isInteger(presentationId) || presentationId <= 0 || !Number.isFinite(qtyInBase) || qtyInBase <= 0) {
        return 0;
      }
      const stockMap = await ensureStockForPresentationIds([presentationId], { forceRefresh: true });
      return getMaxUnitsFromStockBase(stockMap[String(presentationId)], qtyInBase);
    },
    [ensureStockForPresentationIds]
  );

  const getCachedMaxUnitsForItem = useCallback(
    (item) => {
      const stockBase = stockByPresentation[String(item?.product_presentation_id || "")];
      if (stockBase === undefined) return null;
      const qtyInBase = getQtyInBaseForItem(item);
      if (!Number.isFinite(qtyInBase) || qtyInBase <= 0) return null;
      return Math.max(0, Math.floor(Number(stockBase || 0) / qtyInBase));
    },
    [getQtyInBaseForItem, stockByPresentation]
  );

  const getStockChipState = useCallback(
    (item) => {
      const stockBase = Number(stockByPresentation[String(item?.product_presentation_id || "")] || 0);
      const qtyInBase = getQtyInBaseForItem(item);
      const itemId = String(item?.tempId || "");
      const draftQty = qtyDraftByItem[itemId];
      const currentQty = draftQty !== undefined ? Number(draftQty) : Number(item?.qty_presentation || 0);
      const ownReservedBase =
        Number.isFinite(currentQty) && currentQty > 0 && Number.isFinite(qtyInBase) && qtyInBase > 0
          ? currentQty * qtyInBase
          : Number(item?.qty_base || 0);
      const remainingBase = Math.max(0, stockBase - ownReservedBase);
      return getStockAvailabilityMeta(remainingBase, qtyInBase, 1);
    },
    [getQtyInBaseForItem, qtyDraftByItem, stockByPresentation]
  );

  const getStockTooltipContent = useCallback(
    (item, countBlockMeta = null) => {
      const key = String(item?.product_presentation_id || "");
      const detail = stockDetailByPresentation[key] || {};
      const qtyInBase = getQtyInBaseForItem(item);
      const toUnits = (value) => {
        const parsed = Number(value || 0);
        if (!Number.isFinite(parsed) || !Number.isFinite(qtyInBase) || qtyInBase <= 0) return "0";
        return String(Math.max(0, Math.floor(parsed / qtyInBase)));
      };
      const availableExternal = Number(detail?.qty_available ?? stockByPresentation[key] ?? 0);
      const reservedSoft = Number(detail?.qty_reserved_soft ?? 0);
      const reservedHard = Number(detail?.qty_reserved_hard ?? 0);
      const itemId = String(item?.tempId || "");
      const draftQty = qtyDraftByItem[itemId];
      const currentQty = draftQty !== undefined ? Number(draftQty) : Number(item?.qty_presentation || 0);
      const ownReserved =
        Number.isFinite(currentQty) && currentQty > 0 && Number.isFinite(qtyInBase) && qtyInBase > 0
          ? currentQty * qtyInBase
          : Number(item?.qty_base || 0);
      const inventory = Number(detail?.qty_base ?? availableExternal + reservedSoft + reservedHard);
      const available = Math.max(0, availableExternal - ownReserved);

      return (
        <Stack spacing={0.4} sx={{ minWidth: 210 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item?.product_description || "Stock"}</Typography>
          {countBlockMeta?.blocked ? <Typography variant="caption" color="warning.light">{countBlockMeta.detail}</Typography> : null}
          <Divider sx={{ borderColor: "rgba(255,255,255,0.18)" }} />
          <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Stock total</Typography><Typography variant="caption">{toUnits(inventory)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Reservado otros</Typography><Typography variant="caption">{toUnits(reservedSoft + reservedHard)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Reservado propio</Typography><Typography variant="caption">{toUnits(ownReserved)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Disponible</Typography><Typography variant="caption">{toUnits(available)}</Typography></Stack>
        </Stack>
      );
    },
    [getQtyInBaseForItem, qtyDraftByItem, stockByPresentation, stockDetailByPresentation]
  );

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus?.();
  }, []);

  const resetCartState = useCallback(() => {
    setCartItems([]);
    setCartCustomer("");
    setCartCustomerPhone("");
    setCartCustomerId(null);
    setCustomerLookupLoading(false);
    setCustomerLookupStatus("idle");
    setCustomerCreateDialogOpen(false);
    setQtyDraftByItem({});
    setPriceDraftByItem({});
    setLastValidPriceByItem({});
    setEditingPriceItemId(null);
    setPriceOverrideDialog({
      open: false,
      item: null,
      newUnitPrice: null,
      basePrice: null,
      costPrice: null,
    });
  }, []);

  const loadCurrentCash = useCallback(async () => {
    if (!branchId) {
      setCashInfo(null);
      return;
    }

    setLoadingCash(true);
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      setCashInfo(data || null);
    } catch {
      setCashInfo(null);
    } finally {
      setLoadingCash(false);
    }
  }, [branchId]);

  const loadSalesConfig = useCallback(async () => {
    if (!branchId) {
      setSalesConfig({ allow_cashier_queue: true });
      return;
    }
    try {
      const data = await saleService.getSalesConfig({ branch_id: branchId });
      setSalesConfig({
        branch_id: data?.branch_id || branchId,
        allow_cashier_queue: data?.allow_cashier_queue !== false,
      });
    } catch {
      setSalesConfig({ branch_id: branchId, allow_cashier_queue: true });
    }
  }, [branchId]);

  const loadActiveStockCountBlock = useCallback(async () => {
    if (!branchId) {
      setStockCountBlock({
        mode: null,
        countId: null,
        blockedPresentationIds: {},
        blockedProductIds: {},
      });
      return;
    }

    try {
      const settings = await inventoryService.getCountSettings({ branch_id: branchId });
      if (settings?.active_general_inventory?.id) {
        setStockCountBlock({
          mode: "GENERAL",
          countId: settings.active_general_inventory.id,
          blockedPresentationIds: {},
          blockedProductIds: {},
        });
        return;
      }

      const counts = toArray(
        await inventoryService.listStockCounts({
          branch_id: branchId,
          status: "DRAFT",
        })
      );
      const activeCount = counts[0] || null;
      if (!activeCount) {
        setStockCountBlock({
          mode: null,
          countId: null,
          blockedPresentationIds: {},
          blockedProductIds: {},
        });
        return;
      }

      const countType = String(activeCount?.count_type || "").toUpperCase();
      if (countType === "GENERAL") {
        setStockCountBlock({
          mode: "GENERAL",
          countId: activeCount.id,
          blockedPresentationIds: {},
          blockedProductIds: {},
        });
        return;
      }

      const detail = await inventoryService.getStockCount(activeCount.id, { branch_id: branchId });
      const blockedPresentationIds = {};
      const blockedProductIds = {};
      toArray(detail?.items).forEach((item) => {
        const presentationId = Number(item?.product_presentation?.id);
        const productId = Number(item?.product_presentation?.product?.id);
        if (Number.isInteger(presentationId) && presentationId > 0) {
          blockedPresentationIds[String(presentationId)] = true;
        }
        if (Number.isInteger(productId) && productId > 0) {
          blockedProductIds[String(productId)] = true;
        }
      });

      setStockCountBlock({
        mode: "SELECTIVE",
        countId: activeCount.id,
        blockedPresentationIds,
        blockedProductIds,
      });
    } catch {
      setStockCountBlock({
        mode: null,
        countId: null,
        blockedPresentationIds: {},
        blockedProductIds: {},
      });
    }
  }, [branchId]);

  const loadTransferAccounts = useCallback(async () => {
    if (!branchId) {
      setTransferAccounts([]);
      return;
    }
    setLoadingTransferAccounts(true);
    try {
      const data = await saleService.listTransferAccounts({ branch_id: branchId, active: true });
      setTransferAccounts(toArray(data));
    } catch {
      setTransferAccounts([]);
    } finally {
      setLoadingTransferAccounts(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (!open || !customerPhoneQuery) {
      setCustomerLookupLoading(false);
      if (!customerPhoneQuery) {
        setCustomerLookupStatus("idle");
      }
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setCustomerLookupLoading(true);
      try {
        const customer = await customersService.findByPhone(customerPhoneQuery);
        if (cancelled) return;
        setCartCustomer(String(customer?.name || "").trim());
        setCartCustomerId(Number(customer?.id) || null);
          setCartCustomerPhone(normalizePhoneValue(customer?.phone_number || customerPhoneQuery));
        setCustomerLookupStatus("found");
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 404) {
          setCartCustomer("");
          setCartCustomerId(null);
          setCustomerLookupStatus("not_found");
          return;
        }
        setCustomerLookupStatus("idle");
      } finally {
        if (!cancelled) {
          setCustomerLookupLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [customerPhoneQuery, open]);

  useEffect(() => {
    if (!open) return;
    resetCartState();
    setSearchTerm("");
    setSearchResults([]);
    setSearchStockByProduct({});
    setStockByPresentation({});
    stockByPresentationRef.current = {};
    setStockDetailByPresentation({});
    stockDetailByPresentationRef.current = {};
    setPayDialogOpen(false);
    setCloseConfirmOpen(false);
    loadCurrentCash();
    loadSalesConfig();
    loadTransferAccounts();
    loadActiveStockCountBlock();
    focusSearchInput();
  }, [focusSearchInput, loadActiveStockCountBlock, loadCurrentCash, loadSalesConfig, loadTransferAccounts, open, resetCartState]);

  useEffect(() => {
    if (!open) return undefined;
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (!branchId || (payloadBranchId && payloadBranchId !== branchId)) return;
      loadCurrentCash();
    });
    return unsubscribe;
  }, [branchId, loadCurrentCash, open]);

  useEffect(() => {
    if (!open || !branchId) return undefined;
    const query = searchTerm.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchStockByProduct({});
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list(
          {
            search: query,
            active: "true",
            sales_search: "true",
            branch_id: branchId,
            limit: 20,
          },
          { signal: controller.signal }
        );
        if (cancelled) return;

        const results = toArray(data);
        const allPresentationIds = [];
        const optionsByProductId = {};
        results.forEach((product) => {
          productDetailByIdRef.current[String(product.id)] = product;
          const options = buildPresentationOptions(product);
          optionsByProductId[String(product.id)] = options;
          registerPresentationOptions(product.id, options);
          options.forEach((option) => {
            if (Number.isInteger(Number(option?.id)) && Number(option.id) > 0) {
              allPresentationIds.push(Number(option.id));
            }
          });
        });

        const uniquePresentationIds = Array.from(new Set(allPresentationIds));
        const stockRows = uniquePresentationIds.length
          ? toArray(
              await inventoryService.listStocks({
                branch_id: branchId,
                product_presentation_ids: uniquePresentationIds.join(","),
              })
            )
          : [];

        const stockBaseByPresentation = {};
        stockRows.forEach((row) => {
          stockBaseByPresentation[String(row?.product_presentation_id)] = Number(
            row?.qty_available ?? row?.qty_base ?? 0
          );
        });
        uniquePresentationIds.forEach((id) => {
          if (stockBaseByPresentation[String(id)] === undefined) {
            stockBaseByPresentation[String(id)] = 0;
          }
        });
        const stockDetailPatch = buildStockDetailPatch(stockRows);
        mergeStockByPresentation(stockBaseByPresentation);
        mergeStockDetailByPresentation(stockDetailPatch);

        const nextSearchStockByProduct = {};
        results.forEach((product) => {
          const options = optionsByProductId[String(product.id)] || [];
          nextSearchStockByProduct[String(product.id)] = buildProductStockMeta(
            options,
            stockBaseByPresentation,
            stockDetailPatch
          );
        });

        setSearchResults(results);
        setSearchStockByProduct(nextSearchStockByProduct);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }
        if (!cancelled) {
          setSearchResults([]);
          setSearchStockByProduct({});
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [branchId, mergeStockByPresentation, mergeStockDetailByPresentation, open, registerPresentationOptions, searchTerm]);

  useEffect(() => {
    if (!open || !branchId) return;
    const ids = Array.from(
      new Set(
        cartItems
          .map((item) => Number(item?.product_presentation_id))
          .filter((id) => Number.isInteger(id) && id > 0)
      )
    );
    const missing = ids.filter((id) => stockByPresentation[String(id)] === undefined);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const presentationId of missing) {
        if (cancelled) return;
        await fetchStockForPresentation(presentationId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, cartItems, fetchStockForPresentation, open, stockByPresentation]);

  const handleAddProduct = useCallback(
    async (product) => {
      if (!branchId || !product?.id) return;
      const countBlockMeta = getProductCountBlockMeta(product);
      if (countBlockMeta.blocked) {
        showSnack(countBlockMeta.detail, "warning");
        return;
      }
      if (itemLimitReached) {
        showSnack("Venta rapida limitada a 10 lineas por ticket.", "warning");
        return;
      }

      setItemUpdating(true);
      try {
        const options = await loadPresentationOptionsForProduct(product.id);
        if (options.length === 0) {
          showSnack("El producto no tiene presentaciones activas con precio.", "warning");
          return;
        }

        const stockBaseMap = await ensureStockForPresentationIds(options.map((option) => option.id));
        const preferred = selectPreferredPresentationOption(options, stockBaseMap);
        const selectedOption = preferred?.option || options[0];
        const maxUnits = preferred?.maxUnits ?? 0;
        if (!selectedOption || maxUnits <= 0) {
          const stockMeta = buildProductStockMeta(options, stockBaseMap, stockDetailByPresentationRef.current);
          showSnack(
            stockMeta?.status === "reserved"
              ? `${product.description || "Producto"} en reserva. Disponible: 0.`
              : `Sin stock disponible para ${product.description || "este producto"}.`,
            "warning"
          );
          return;
        }

        setCartItems((prev) => {
          const existingIndex = prev.findIndex(
            (item) => String(item.product_presentation_id) === String(selectedOption.id)
          );
          if (existingIndex >= 0) {
            const next = [...prev];
            const existing = next[existingIndex];
            const nextQty = Number(existing.qty_presentation || 0) + 1;
            if (nextQty > maxUnits) {
              showSnack(`Stock maximo disponible: ${maxUnits}.`, "warning");
              return prev;
            }
            next[existingIndex] = normalizeCartItem({
              ...existing,
              stock_available: maxUnits,
              qty_presentation: nextQty,
            });
            return next;
          }

          return [
            ...prev,
            normalizeCartItem({
              tempId: createTempId(),
              product_presentation_id: selectedOption.id,
              product_id: product.id,
              product_description: product.description || "Producto",
              presentation_name: selectedOption.name || "Presentacion",
              qty_in_base: Number(selectedOption.qty_in_base || 1),
              qty_presentation: 1,
              unit_price: Number(selectedOption.base_sale_price || 0),
              cost_price: Number(selectedOption.cost_price || 0),
              base_price: Number(selectedOption.base_sale_price || 0),
              stock_available: maxUnits,
            }),
          ];
        });
        setSearchTerm("");
        setSearchResults([]);
        focusSearchInput();
      } catch {
        showSnack("No se pudo agregar el producto.", "error");
      } finally {
        setItemUpdating(false);
      }
    },
    [
      branchId,
      ensureStockForPresentationIds,
      focusSearchInput,
      getProductCountBlockMeta,
      itemLimitReached,
      loadPresentationOptionsForProduct,
      showSnack,
    ]
  );

  const handleRemoveItem = useCallback((item) => {
    const key = String(item?.tempId || "");
    setCartItems((prev) => prev.filter((row) => row.tempId !== item?.tempId));
    setQtyDraftByItem((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPriceDraftByItem((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setLastValidPriceByItem((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (editingPriceItemId === key) {
      setEditingPriceItemId(null);
    }
  }, [editingPriceItemId]);

  const commitQtyChange = useCallback(
    async (item) => {
      const key = String(item?.tempId || "");
      const rawValue = qtyDraftByItem[key];
      if (rawValue === undefined) return;

      const qtyValue = Number(rawValue);
      const currentQty = Number(item?.qty_presentation || 1);
      if (!Number.isFinite(qtyValue) || qtyValue <= 0 || !Number.isInteger(qtyValue)) {
        setQtyDraftByItem((prev) => ({ ...prev, [key]: String(Math.max(1, currentQty)) }));
        showSnack("Cantidad invalida. Debe ser un entero mayor a 0.", "warning");
        return;
      }

      const options = getItemOptions(item);
      const selected = options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
      const maxUnits = await getMaxUnitsForOption(selected);
      if (maxUnits <= 0) {
        showSnack(`Sin stock disponible para ${item?.product_description || "este producto"}.`, "warning");
        setQtyDraftByItem((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      const safeQty = Math.min(qtyValue, maxUnits);
      if (safeQty < qtyValue) {
        showSnack(`Cantidad ajustada a stock disponible: ${safeQty}.`, "warning");
      }

      setCartItems((prev) =>
        prev.map((row) =>
          row.tempId === item.tempId
            ? normalizeCartItem({
                ...row,
                qty_presentation: safeQty,
                stock_available: maxUnits,
              })
            : row
        )
      );
      setQtyDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [getItemOptions, getMaxUnitsForOption, qtyDraftByItem, showSnack]
  );

  const handlePresentationChange = useCallback(
    async (item, nextPresentationId) => {
      if (!item?.tempId || !nextPresentationId || String(nextPresentationId) === String(item.product_presentation_id)) {
        return;
      }

      const options = await loadPresentationOptionsForProduct(item.product_id);
      const nextOption = options.find((row) => String(row.id) === String(nextPresentationId));
      if (!nextOption) {
        showSnack(`Sin stock disponible para ${item?.product_description || "esta presentacion"}.`, "warning");
        return;
      }

      const externalStockMap = await ensureStockForPresentationIds([nextPresentationId], {
        forceRefresh: true,
      });
      const externalStockBase = Number(externalStockMap[String(nextPresentationId)] || 0);
      const newQtyInBase = Number(nextOption?.qty_in_base || 1);
      const maxUnits =
        Number.isFinite(newQtyInBase) && newQtyInBase > 0
          ? Math.floor(externalStockBase / newQtyInBase)
          : 0;
      if (!nextOption || maxUnits <= 0) {
        showSnack("Sin stock disponible para este formato. Disponible: 0.", "warning");
        return;
      }

      const currentQty = Math.max(1, parseInt(item?.qty_presentation || 1, 10));
      const safeQty = Math.min(currentQty, maxUnits);
      if (safeQty < currentQty) {
        showSnack(`Cantidad ajustada a ${safeQty} (maximo disponible en este formato).`, "warning");
      }

      setCartItems((prev) => {
        const mergeTarget = prev.find(
          (row) =>
            row.tempId !== item.tempId &&
            String(row.product_presentation_id) === String(nextPresentationId)
        );
        if (mergeTarget) {
          const mergedQty = Math.min(Number(mergeTarget.qty_presentation || 0) + safeQty, maxUnits);
          return prev
            .filter((row) => row.tempId !== item.tempId)
            .map((row) =>
              row.tempId === mergeTarget.tempId
                ? normalizeCartItem({
                    ...row,
                    qty_presentation: mergedQty,
                    stock_available: maxUnits,
                  })
                : row
            );
        }

        return prev.map((row) =>
          row.tempId === item.tempId
            ? normalizeCartItem({
                ...row,
                product_presentation_id: nextOption.id,
                presentation_name: nextOption.name || "Presentacion",
                qty_in_base: Number(nextOption.qty_in_base || 1),
                qty_presentation: safeQty,
                unit_price: Number(nextOption.base_sale_price || row.unit_price || 0),
                cost_price: Number(nextOption.cost_price || 0),
                base_price: Number(nextOption.base_sale_price || row.base_price || 0),
                stock_available: maxUnits,
              })
            : row
        );
      });
      setEditingPriceItemId(null);
    },
    [ensureStockForPresentationIds, loadPresentationOptionsForProduct, showSnack]
  );

  const handlePriceDraftChange = useCallback((item, rawValue) => {
    setPriceDraftByItem((prev) => ({ ...prev, [String(item.tempId)]: rawValue }));
  }, []);

  const handleStartPriceEdit = useCallback((item) => {
    const key = String(item.tempId);
    const current = Number(item?.unit_price || 0).toFixed(2);
    setPriceDraftByItem((prev) => ({ ...prev, [key]: prev[key] ?? current }));
    setEditingPriceItemId(key);
  }, []);

  const commitPriceChange = useCallback(
    async (item) => {
      const key = String(item?.tempId || "");
      const rawValue = priceDraftByItem[key];
      if (rawValue === undefined) return "noop";

      const { costPrice, basePrice } = getItemPricingContext(item);
      const fallback = Number(lastValidPriceByItem[key] ?? item.unit_price ?? basePrice);
      const fallbackNormalized = Number.isFinite(fallback)
        ? Number(fallback).toFixed(2)
        : Number(basePrice).toFixed(2);
      const normalizedRaw = String(rawValue ?? "").trim();
      const parsed = Number(normalizedRaw);

      if (!normalizedRaw || !Number.isFinite(parsed)) {
        setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
        setEditingPriceItemId(null);
        return "restored";
      }
      if (parsed < costPrice) {
        showSnack("No puede vender por debajo del costo", "warning");
        setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
        setEditingPriceItemId(null);
        return "blocked";
      }
      if (parsed < basePrice && !canOverridePrice) {
        showSnack("No autorizado para aplicar descuento", "warning");
        setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
        setEditingPriceItemId(null);
        return "blocked";
      }

      const normalized = Number(parsed).toFixed(2);
      if (normalized === Number(item?.unit_price || 0).toFixed(2)) {
        setPriceDraftByItem((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setEditingPriceItemId(null);
        return "unchanged";
      }
      if (Number(parsed) < Number(basePrice) && canOverridePrice) {
        setPriceOverrideDialog({
          open: true,
          item,
          newUnitPrice: Number(parsed),
          basePrice: Number(basePrice),
          costPrice: Number(costPrice),
        });
        setEditingPriceItemId(null);
        return "override";
      }

      setCartItems((prev) =>
        prev.map((row) =>
          row.tempId === item.tempId
            ? normalizeCartItem({ ...row, unit_price: Number(normalized) })
            : row
        )
      );
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditingPriceItemId(null);
      return "applied";
    },
    [canOverridePrice, getItemPricingContext, lastValidPriceByItem, priceDraftByItem, showSnack]
  );

  const handleConfirmPriceOverride = useCallback(
    async (overridePrice) => {
      const item = priceOverrideDialog.item;
      if (!item?.tempId) {
        setPriceOverrideDialog((prev) => ({ ...prev, open: false }));
        return;
      }

      const key = String(item.tempId);
      const { costPrice } = getItemPricingContext(item);
      const parsed = Number(overridePrice);
      if (!Number.isFinite(parsed) || parsed < costPrice) {
        showSnack("No puede vender por debajo del costo", "warning");
        return;
      }

      const normalized = parsed.toFixed(2);
      setCartItems((prev) =>
        prev.map((row) =>
          row.tempId === item.tempId
            ? normalizeCartItem({ ...row, unit_price: Number(normalized) })
            : row
        )
      );
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPriceOverrideDialog({
        open: false,
        item: null,
        newUnitPrice: null,
        basePrice: null,
        costPrice: null,
      });
      setEditingPriceItemId(null);
      showSnack("Precio actualizado con override autorizado.", "success");
    },
    [getItemPricingContext, priceOverrideDialog.item, showSnack]
  );

  const validateDraftPricesBeforeSubmit = useCallback(async () => {
    if (editingPriceItemId) {
      const editingItem = cartItems.find((row) => String(row.tempId) === String(editingPriceItemId));
      if (editingItem) {
        const commitResult = await commitPriceChange(editingItem);
        if (commitResult === "override" || commitResult === "blocked" || commitResult === "error") {
          return false;
        }
      }
    }

    for (const item of cartItems) {
      const key = String(item.tempId);
      const draft = priceDraftByItem[key];
      if (draft === undefined) continue;
      const normalizedRaw = String(draft ?? "").trim();
      const parsed = Number(normalizedRaw);
      if (!normalizedRaw || !Number.isFinite(parsed)) {
        showSnack(`Precio pendiente para ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      const { costPrice } = getItemPricingContext(item);
      if (parsed < costPrice) {
        showSnack(`Precio no puede ser menor al costo en ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      if (Number(parsed).toFixed(2) !== Number(item?.unit_price || 0).toFixed(2)) {
        showSnack(`Confirma el precio editado de ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
    }

    return true;
  }, [cartItems, commitPriceChange, editingPriceItemId, getItemPricingContext, priceDraftByItem, showSnack]);

  const buildPaymentsPayload = useCallback(
    (paymentPayload) => {
      if (!paymentPayload) return [];
      const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
      const referenceParts = [paymentPayload?.reference || ""];
      if (paymentPayload?.bank) {
        referenceParts.push(`Banco: ${paymentPayload.bank}`);
      }
      if (paymentPayload?.payment_date) {
        referenceParts.push(`Fecha: ${paymentPayload.payment_date}`);
      }
      return [
        {
          payment_method: method,
          amount: cartTotal.toFixed(2),
          cash_received: method === "CASH" ? paymentPayload?.cash_received || cartTotal.toFixed(2) : undefined,
          reference: referenceParts.filter(Boolean).join(" | "),
          account_id: method === "TRANSFER" ? paymentPayload?.account_id : undefined,
        },
      ];
    },
    [cartTotal]
  );

  const buildConfirmCartPayload = useCallback(
    (targetStatus) => ({
      branch_id: branchId,
      customer_id: cartCustomerId ?? null,
      notes: undefined,
      items: cartItems.map((item) => ({
        product_presentation_id: item.product_presentation_id,
        qty_presentation: Number(item.qty_presentation || 0),
        unit_price: Number(item.unit_price || 0).toFixed(2),
      })),
      target_status: targetStatus,
    }),
    [branchId, cartCustomerId, cartItems]
  );

  const finishSuccessfulSubmission = useCallback(
    (result, successMessage, severity = "success") => {
      resetCartState();
      setPayDialogOpen(false);
      setSearchTerm("");
      setSearchResults([]);
      setSearchStockByProduct({});
      showSnack(successMessage, severity);
      emitCashSessionChanged({
        branchId,
        action: String(result?.status || "").toUpperCase() === "PENDING" ? "sale_pending" : "sale_paid",
        saleId: result?.id,
      });
      onClose?.();
    },
    [branchId, onClose, resetCartState, showSnack]
  );

  const submitCart = useCallback(
    async (targetStatus, paymentPayload = null) => {
      if (!branchId) return;
      if (cartItems.length === 0) {
        showSnack("Agrega al menos un item al carrito.", "warning");
        return;
      }
      if (hasBlockedCartItems) {
        showSnack(
          stockCountBlock.mode === "GENERAL"
            ? GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE
            : SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
          "warning"
        );
        return;
      }
      if (!hasOpenCashSession) {
        showSnack("Debes abrir caja antes de confirmar el pedido.", "warning");
        return;
      }
      if (targetStatus === "PENDING" && !cashierQueueEnabled) {
        showSnack("La bandeja de caja de cobro no esta habilitada para esta sucursal.", "warning");
        return;
      }

      const pricesReady = await validateDraftPricesBeforeSubmit();
      if (!pricesReady) return;

      const invalidItem = cartItems.find((item) => {
        const qty = Number(item?.qty_presentation);
        return !item?.product_presentation_id || !Number.isInteger(qty) || qty <= 0;
      });
      if (invalidItem) {
        showSnack("Revisa presentaciones y cantidades (solo enteros) antes de continuar.", "warning");
        return;
      }

      const payload = buildConfirmCartPayload(targetStatus);
      if (targetStatus === "IN_CASHIER") {
        setPaySubmitting(true);
      } else {
        setSendSubmitting(true);
      }

      try {
        const confirmedSale = await saleService.confirmCart(payload);
        if (targetStatus === "IN_CASHIER") {
          let paidSale = null;
          const paymentMethod = String(paymentPayload?.payment_method || "CASH").toUpperCase();
          const shouldAutoPrint = paymentMethod === "CASH" || paymentMethod === "CARD" || paymentMethod === "VOUCHER";
          try {
            paidSale = await saleService.paySale(confirmedSale.id, {
              branch_id: branchId,
              payments: buildPaymentsPayload(paymentPayload),
            });
            if (String(paidSale?.status || "").toUpperCase() === "COMPLETED" && shouldAutoPrint) {
              await handlePostSaleInvoiceFlow({
                sale: paidSale,
                branchName: paidSale?.branch_name || currentBranch?.name || "-",
                cashierName: getTicketCashierName(paidSale),
                allowTicketPrint: shouldAutoPrint,
              });
            }
          } catch (paymentError) {
            await saleService.deleteSale(confirmedSale.id, branchId, { force: true }).catch(() => {});
            throw paymentError;
          }
          finishSuccessfulSubmission(paidSale, "Venta cobrada correctamente.", "success");
          return;
        }
        finishSuccessfulSubmission(confirmedSale, "Venta enviada a caja.", "success");
      } catch (error) {
        showSnack(
          extractErrorMessage(
            error,
            targetStatus === "IN_CASHIER"
              ? "No se pudo cobrar la venta."
              : "No se pudo enviar la venta a caja."
          ),
          "error"
        );
      } finally {
        if (targetStatus === "IN_CASHIER") {
          setPaySubmitting(false);
        } else {
          setSendSubmitting(false);
        }
      }
    },
    [
      branchId,
      buildConfirmCartPayload,
      buildPaymentsPayload,
      cartItems,
      currentBranch?.name,
      finishSuccessfulSubmission,
      handlePostSaleInvoiceFlow,
      hasBlockedCartItems,
      hasOpenCashSession,
      cashierQueueEnabled,
      stockCountBlock.mode,
      showSnack,
      validateDraftPricesBeforeSubmit,
    ]
  );

  const handleSendToCash = useCallback(async () => {
    await submitCart("PENDING");
  }, [submitCart]);

  const handleConfirmPay = useCallback(async (paymentPayload) => {
    await submitCart("IN_CASHIER", paymentPayload);
  }, [submitCart]);

  const forceCloseDialog = useCallback(() => {
    resetCartState();
    setSearchTerm("");
    setSearchResults([]);
    setSearchStockByProduct({});
    setPayDialogOpen(false);
    setCloseConfirmOpen(false);
    onClose?.();
  }, [onClose, resetCartState]);

  const handleRequestClose = useCallback(() => {
    if (hasCartItems) {
      setCloseConfirmOpen(true);
      return;
    }
    forceCloseDialog();
  }, [forceCloseDialog, hasCartItems]);

  const handleDialogClose = useCallback(
    (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        handleRequestClose();
        return;
      }
      handleRequestClose();
    },
    [handleRequestClose]
  );

  const handleConfirmCloseWithItems = useCallback(() => {
    forceCloseDialog();
  }, [forceCloseDialog]);

  const handleTransferToSales = useCallback(async () => {
    if (cartItems.length === 0) return;
    if (hasBlockedCartItems) {
      showSnack(
        stockCountBlock.mode === "GENERAL"
          ? GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE
          : SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        "warning"
      );
      return;
    }
    if (!hasOpenCashSession) {
      showSnack("Debes abrir caja antes de trasladar a venta normal.", "warning");
      return;
    }
    const pricesReady = await validateDraftPricesBeforeSubmit();
    if (!pricesReady) return;

    setSendSubmitting(true);
    let confirmedSale = null;
    try {
      confirmedSale = await saleService.confirmCart(buildConfirmCartPayload("IN_CASHIER"));
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo captar stock para trasladar la venta."), "error");
      setSendSubmitting(false);
      return;
    }

    const draftId = createSalesDraftId();
    dispatch(
      saveSalesDraft({
        key: getSalesDraftStorageKey(`?draft=${draftId}`),
        draft: {
          saleHeader: {
            customer: String(cartCustomer || "").trim(),
            customer_id: cartCustomerId ?? null,
            customer_phone: String(cartCustomerPhone || "").trim(),
            notes: "",
            sale_date: nowDateString(),
            branch_name: currentBranch?.name || "",
          },
          sale: confirmedSale,
          cartItemsFromPOS: [],
          searchTerm: "",
          pinSearchResults: false,
          qtyDraftByItem: {},
          priceDraftByItem: {},
          lastValidPriceByItem: {},
        },
      })
    );
    resetCartState();
    setSearchTerm("");
    setSearchResults([]);
    setSearchStockByProduct({});
    setSendSubmitting(false);
    onClose?.();
    navigate(buildSalesDraftPath(draftId));
  }, [
    buildConfirmCartPayload,
    cartCustomer,
    cartCustomerId,
    cartCustomerPhone,
    cartItems,
    currentBranch?.name,
    dispatch,
    hasOpenCashSession,
    hasBlockedCartItems,
    navigate,
    onClose,
    resetCartState,
    showSnack,
    stockCountBlock.mode,
    validateDraftPricesBeforeSubmit,
  ]);

  const handleOpenCashFromPos = useCallback(async () => {
    if (!branchId) return;
    if (!canOpenCash) {
      showSnack("No autorizado para abrir caja desde esta vista.", "warning");
      return;
    }
    if (cashOpenBlockedByGeneralInventory) {
      showSnack("No puedes abrir caja durante inventario general.", "warning");
      return;
    }

    setOpenCashSubmitting(true);
    try {
      await cashService.openSession({
        opening_float: openCashForm.opening_float || "0",
        opening_note: openCashForm.opening_note || "Apertura desde POS",
      });
      setOpenCashDialogOpen(false);
      setOpenCashForm({ opening_float: "0.00", opening_note: "" });
      emitCashSessionChanged({ branchId, action: "open_from_pos" });
      await loadCurrentCash();
      showSnack("Caja abierta correctamente.", "success");
      focusSearchInput();
    } catch (error) {
      const statusCode = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (statusCode === 409) {
        if (detail && !error?.response?.data?.session) {
          await loadActiveStockCountBlock();
          showSnack(detail, "error");
          return;
        }
        setOpenCashDialogOpen(false);
        setOpenCashForm({ opening_float: "0.00", opening_note: "" });
        emitCashSessionChanged({ branchId, action: "open_existing_from_pos" });
        await loadCurrentCash();
        showSnack("La caja ya estaba abierta.", "info");
        focusSearchInput();
        return;
      }
      showSnack(detail || "No se pudo abrir la caja.", "error");
    } finally {
      setOpenCashSubmitting(false);
    }
  }, [
    branchId,
    canOpenCash,
    cashOpenBlockedByGeneralInventory,
    focusSearchInput,
    loadActiveStockCountBlock,
    loadCurrentCash,
    openCashForm.opening_float,
    openCashForm.opening_note,
    showSnack,
  ]);

  const dialogBusy = itemUpdating || sendSubmitting || paySubmitting || loadingCash;

  return (
    <>
      <Dialog open={Boolean(open)} onClose={handleDialogClose} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {cartItems.length === 0 ? "Consulta de precios" : "Venta rapida"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cartItems.length === 0
                ? "Consulta existencias y precios antes de agregar productos."
                : "Carrito local listo para enviar, cobrar o trasladar a venta normal."}
            </Typography>
          </Box>
          <IconButton onClick={handleRequestClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: isMobile ? "1fr" : "1.15fr 0.85fr",
              alignItems: "start",
            }}
          >
            <Stack spacing={1.2}>
              {stockCountBlock.mode === "GENERAL" ? (
                <Alert severity="error" variant="outlined">
                  {GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE}
                </Alert>
              ) : null}
              {stockCountBlock.mode === "SELECTIVE" ? (
                <Alert severity="warning" variant="outlined">
                  Hay productos en conteo selectivo. Puedes consultarlos, pero no agregarlos al carrito mientras sigan en conteo.
                </Alert>
              ) : null}

              <Stack spacing={1}>
                <TextField
                  label="Telefono del cliente"
                  size="small"
                  placeholder="Opcional"
                  value={cartCustomerPhone}
                  onChange={handleCustomerPhoneChange}
                  inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
                  fullWidth
                />
                <TextField
                  label="Cliente asociado"
                  size="small"
                  value={customerDisplayValue}
                  helperText={customerLookupHelperText}
                  fullWidth
                  disabled
                />
                {customerPhoneQuery && customerLookupStatus === "not_found" ? (
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setCustomerCreateDialogOpen(true)}
                      disabled={customerLookupLoading}
                    >
                      Crear cliente
                    </Button>
                  </Stack>
                ) : null}
              </Stack>

              <TextField
                inputRef={searchInputRef}
                label="Buscar producto"
                size="small"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && searchResults.length > 0) {
                    event.preventDefault();
                    handleAddProduct(searchResults[0]);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  stockCountBlock.mode
                    ? "Puedes consultar productos, pero los que esten en conteo apareceran bloqueados."
                    : "Enter agrega el primer resultado"
                }
                fullWidth
              />

              {searchTerm.trim().length >= 2 && (
                <Paper variant="outlined" sx={{ maxHeight: 190, overflowY: "auto", p: 1 }}>
                  {searchLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Buscando...
                    </Typography>
                  ) : searchResults.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Sin resultados.
                    </Typography>
                  ) : (
                    <Stack spacing={0.7}>
                      {searchResults.map((row) => {
                        const stockMeta = searchStockByProduct[String(row.id)];
                        const stockLabel = stockMeta?.label || "Sin stock";
                        const alreadyAdded = addedProductIds.has(String(row.id));
                        const countBlockMeta = getProductCountBlockMeta(row);
                        return (
                          <Stack
                            key={row.id}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              p: 0.8,
                              borderRadius: 1,
                              bgcolor: alreadyAdded
                                ? alpha(theme.palette.success.main, 0.06)
                                : "background.default",
                              borderLeft: alreadyAdded
                                ? `3px solid ${theme.palette.success.main}`
                                : "3px solid transparent",
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {row.description || "Producto"}
                              </Typography>
                              {alreadyAdded ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "success.main", fontWeight: 600, fontSize: "0.7rem", display: "block" }}
                                >
                                  ✓ Ya en carrito
                                </Typography>
                              ) : null}
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {row.code || "-"}
                              </Typography>
                              {stockMeta ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                                  {stockMeta?.presentationName || "Presentacion"} - {toMoney(stockMeta?.unit_price)}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.4 }}>
                                <Tooltip title={stockMeta?.tooltip || "Stock total: 0. Reservado: 0. Disponible: 0."}>
                                  <Chip
                                    size="small"
                                    color={stockMeta?.color || "default"}
                                    variant="outlined"
                                    label={stockLabel}
                                  />
                                </Tooltip>
                                {countBlockMeta.blocked ? (
                                  <Tooltip title={countBlockMeta.detail}>
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="filled"
                                      label={countBlockMeta.label}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Stack>
                            </Box>
                            <Button
                              size="small"
                              variant={countBlockMeta.blocked ? "text" : "outlined"}
                              color={countBlockMeta.blocked ? "warning" : alreadyAdded ? "success" : "primary"}
                              startIcon={<AddShoppingCart fontSize="small" />}
                              onClick={() => handleAddProduct(row)}
                              disabled={dialogBusy || countBlockMeta.blocked || !stockMeta?.canFulfillRequested}
                            >
                              {countBlockMeta.blocked
                                ? "No disponible"
                                : alreadyAdded
                                  ? "Agregar mas"
                                  : "Agregar"}
                            </Button>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>
              )}

              <Paper variant="outlined" sx={{ minHeight: 280, maxHeight: "calc(100vh - 360px)", overflow: "auto", p: 1 }}>
                {cartItems.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" spacing={0.8} sx={{ minHeight: 220, textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Carrito vacio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Busca productos y agregalos para confirmar la venta.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    {cartItems.map((item) => {
                      const itemId = String(item.tempId);
                      const qtyValue = qtyDraftByItem[itemId] ?? String(parseInt(item.qty_presentation || 0, 10));
                      const priceValue = priceDraftByItem[itemId] ?? Number(item.unit_price || 0).toFixed(2);
                      const { options } = getItemPricingContext(item);
                      const countBlockMeta = getItemCountBlockMeta(item);
                      const stockChip = countBlockMeta.blocked
                        ? { color: "warning", label: countBlockMeta.label }
                        : getStockChipState(item);

                      return (
                        <Paper key={item.tempId} variant="outlined" sx={{ p: 1 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                  {item.product_description}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Base: {Number(item.qty_in_base || 0).toFixed(3)}
                                </Typography>
                              </Box>
                              <IconButton size="small" color="error" onClick={() => handleRemoveItem(item)} disabled={dialogBusy}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} alignItems={{ sm: "center" }}>
                              <TextField
                                label="Cantidad"
                                size="small"
                                type="number"
                                disabled={countBlockMeta.blocked || dialogBusy}
                                value={qtyValue}
                                onChange={(event) => {
                                  const sanitized = sanitizeIntegerInput(event.target.value);
                                  const cachedMaxUnits = getCachedMaxUnitsForItem(item);
                                  const nextValue =
                                    sanitized && Number.isInteger(cachedMaxUnits)
                                      ? cachedMaxUnits > 0
                                        ? String(Math.min(Number(sanitized), cachedMaxUnits))
                                        : sanitized
                                      : sanitized;
                                  setQtyDraftByItem((prev) => ({ ...prev, [itemId]: nextValue }));
                                  const nextQty = Number(nextValue);
                                  if (Number.isInteger(nextQty) && nextQty > 0) {
                                    setCartItems((prev) =>
                                      prev.map((row) =>
                                        row.tempId === item.tempId
                                          ? normalizeCartItem({
                                              ...row,
                                              qty_presentation: nextQty,
                                              stock_available:
                                                Number.isInteger(cachedMaxUnits) && cachedMaxUnits > 0
                                                  ? cachedMaxUnits
                                                  : row.stock_available,
                                            })
                                          : row
                                      )
                                    );
                                  }
                                }}
                                onBlur={() => commitQtyChange(item)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    commitQtyChange(item);
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  step: "1",
                                  max:
                                    Number.isInteger(getCachedMaxUnitsForItem(item)) &&
                                    getCachedMaxUnitsForItem(item) > 0
                                      ? getCachedMaxUnitsForItem(item)
                                      : undefined,
                                }}
                                sx={{ width: 120 }}
                              />
                              <TextField
                                select
                                label="Formato"
                                size="small"
                                disabled={countBlockMeta.blocked || dialogBusy}
                                value={item.product_presentation_id || ""}
                                onChange={(event) => handlePresentationChange(item, Number(event.target.value))}
                                sx={{ minWidth: 150 }}
                              >
                                {options.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {option.name} ({Number(option.qty_in_base || 0).toFixed(3)})
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Tooltip title={getStockTooltipContent(item, countBlockMeta)} arrow placement="top">
                                <Chip size="small" color={stockChip.color} label={countBlockMeta.blocked ? stockChip.label : `Stock ${stockChip.label}`} variant="outlined" />
                              </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={0.8} alignItems="center">
                              {editingPriceItemId === itemId ? (
                                <TextField
                                  label="Precio"
                                  size="small"
                                  type="number"
                                  disabled={countBlockMeta.blocked || dialogBusy}
                                  value={priceValue}
                                  autoFocus
                                  onFocus={(event) => event.target.select()}
                                  onChange={(event) => handlePriceDraftChange(item, event.target.value)}
                                  onBlur={() => commitPriceChange(item)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      commitPriceChange(item);
                                    }
                                  }}
                                  inputProps={{ step: "0.01" }}
                                  sx={{ width: 130 }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    cursor: countBlockMeta.blocked ? "default" : "pointer",
                                    color: countBlockMeta.blocked ? "text.secondary" : "text.primary",
                                  }}
                                  onClick={() => {
                                    if (countBlockMeta.blocked) return;
                                    handleStartPriceEdit(item);
                                  }}
                                >
                                  {toMoney(item.unit_price)}
                                </Typography>
                              )}
                              <Box sx={{ ml: "auto" }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {toMoney(item.line_total)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Stack>

            <Stack spacing={1.2}>
              {!hasOpenCashSession && (
                <Alert
                  severity="warning"
                  action={
                    canOpenCash ? (
                      <Tooltip
                        title={
                          cashOpenBlockedByGeneralInventory
                            ? "No puedes abrir caja durante inventario general."
                            : ""
                        }
                      >
                        <span>
                          <Button
                            color="inherit"
                            size="small"
                            disabled={cashOpenBlockedByGeneralInventory}
                            onClick={() => setOpenCashDialogOpen(true)}
                          >
                            Abrir caja
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null
                  }
                >
                  {canOpenCash
                    ? "Caja cerrada. Debes abrir una sesion para cobrar."
                    : "Caja cerrada. Puedes consultar productos, pero no cobrar ni abrir caja desde esta vista."}
                </Alert>
              )}
              {!cashierQueueEnabled && !canPayNow ? (
                <Alert severity="warning">
                  Esta sucursal no usa bandeja de cobro. Asigna el permiso "Cobrar al instante" para poder completar ventas.
                </Alert>
              ) : null}

              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Resumen
                </Typography>
                <Stack spacing={0.6}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Items
                    </Typography>
                    <Typography variant="body2">{cartItems.length}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2">{toMoney(cartSubtotal)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
                      Total
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
                      {toMoney(cartTotal)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="text"
                  onClick={handleTransferToSales}
                  disabled={cartItems.length === 0 || dialogBusy || hasBlockedCartItems}
                >
                  Trasladar a venta normal
                </Button>
                {cashierQueueEnabled ? (
                  <Button
                    variant="outlined"
                    onClick={handleSendToCash}
                    disabled={cartItems.length === 0 || dialogBusy || !hasOpenCashSession || hasBlockedCartItems}
                  >
                    Enviar a Caja
                  </Button>
                ) : null}
                {canPayNow ? (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setPayDialogOpen(true)}
                    disabled={cartItems.length === 0 || dialogBusy || !hasOpenCashSession || hasBlockedCartItems}
                  >
                    Cobrar
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleRequestClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <SalePaymentDialog
        open={payDialogOpen}
        onCancel={() => {
          if (paySubmitting) return;
          setPayDialogOpen(false);
        }}
        onConfirm={handleConfirmPay}
        total={cartTotal}
        itemsCount={cartItems.length}
        hasOpenCashSession={hasOpenCashSession}
        transferAccounts={transferAccounts}
        loadingTransferAccounts={loadingTransferAccounts}
        submitting={paySubmitting}
      />

      <PriceOverrideDialog
        open={priceOverrideDialog.open}
        data={priceOverrideDialog}
        onCancel={() => {
          const itemId = priceOverrideDialog?.item?.tempId;
          if (itemId) {
            const basePrice = Number(priceOverrideDialog?.basePrice || 0).toFixed(2);
            setPriceDraftByItem((prev) => ({ ...prev, [String(itemId)]: basePrice }));
          }
          setPriceOverrideDialog({
            open: false,
            item: null,
            newUnitPrice: null,
            basePrice: null,
            costPrice: null,
          });
          setEditingPriceItemId(null);
        }}
        onConfirm={handleConfirmPriceOverride}
        submitting={false}
      />

      <Dialog
        open={canOpenCash && openCashDialogOpen && !cashOpenBlockedByGeneralInventory}
        onClose={() => setOpenCashDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Abrir caja desde POS</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2} sx={{ pt: 0.4 }}>
            <Typography variant="body2" color="text.secondary">
              Caja: {cashInfo?.cash_register?.name || "Principal"}
            </Typography>
            <TextField
              label="Fondo inicial"
              size="small"
              type="number"
              value={openCashForm.opening_float}
              onChange={(event) => setOpenCashForm((prev) => ({ ...prev, opening_float: event.target.value }))}
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              label="Nota"
              size="small"
              multiline
              minRows={2}
              value={openCashForm.opening_note}
              onChange={(event) => setOpenCashForm((prev) => ({ ...prev, opening_note: event.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCashDialogOpen(false)} disabled={openCashSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenCashFromPos}
            disabled={openCashSubmitting || !canOpenCash || cashOpenBlockedByGeneralInventory}
          >
            Abrir caja
          </Button>
        </DialogActions>
      </Dialog>

      <CustomerQuickCreateDialog
        open={customerCreateDialogOpen}
        phoneNumber={cartCustomerPhone}
        onClose={() => setCustomerCreateDialogOpen(false)}
        onCreated={(customer) => handleCustomerSelected(customer, "Cliente creado y asociado.")}
      />

      <Dialog
        open={closeConfirmOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCloseConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirmar cierre</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Hay productos agregados. ¿Deseas cerrar y perder la venta?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmCloseWithItems}>
            Cerrar y perder venta
          </Button>
        </DialogActions>
      </Dialog>

      {invoiceDialog}
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </>
  );
}
